"use client"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Slider } from "@/components/ui/slider"
import {
  Waves,
  Wind,
  Thermometer,
  Users,
  Star,
  TrendingUp,
  Eye,
  MapPin,
  Globe,
  DollarSign,
  Wifi,
  Shield,
  Activity,
  Compass,
  Clock,
  AlertTriangle,
  Heart,
  Car,
  Plane,
  Navigation,
  ChevronDown,
  ChevronRight,
  Leaf,
  Trash2,
  Anchor,
  Sun,
  Cloud,
  Snowflake,
  Utensils,
  Coffee,
  Music,
  Baby,
  Locate,
  Loader2,
  Check,
  RotateCcw,
  Sparkles,
} from "lucide-react"
import { useState, useEffect } from "react"
import { useAuth } from "@/lib/auth/context"
import { useRouter } from "next/navigation"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"
import { cn } from "@/lib/utils"

interface FilterSidebarProps {
  showMobileFilters?: boolean
  setShowMobileFilters?: (show: boolean) => void
  onNearMeToggle?: (enabled: boolean) => void
  nearMeEnabled?: boolean
  nearMeLoading?: boolean
  nearMeError?: string | null
  nearMeRadius?: number
  onNearMeRadiusChange?: (radius: number) => void
  onFiltersApply?: (filters: FilterState) => void
}

export interface FilterState {
  activityType: "surfing" | "kitesurfing" | "both" | null
  breakTypes: string[]
  skillLevels: string[]
  swellDirections: string[]
  windDirections: string[]
  tides: string[]
  crowdLevels: string[]
  parking: string[]
  launchTypes: string[]
  waveConditions: string[]
  windConditions: string[]
  waterConditions: string[]
  safety: string[]
  seaFloor: string[]
  environmental: string[]
  experience: string[]
  cost: string[]
  amenities: string[]
  seasons: string[]
  climate: string[]
  food: string[]
  alternativeActivities: string[]
  regions: string[]
  overallScore: number
  city: string
  cityRadius: number
}

export function FilterSidebar({
  showMobileFilters,
  setShowMobileFilters,
  onNearMeToggle,
  nearMeEnabled = false,
  nearMeLoading = false,
  nearMeError = null,
  nearMeRadius = 50,
  onNearMeRadiusChange,
  onFiltersApply,
}: FilterSidebarProps) {
  const { user, loading: authLoading } = useAuth()
  const router = useRouter()
  const { isMobile } = usePlatform()
  
  const [filterState, setFilterState] = useState<FilterState>({
    activityType: null,
    breakTypes: [],
    skillLevels: [],
    swellDirections: [],
    windDirections: [],
    tides: [],
    crowdLevels: [],
    parking: [],
    launchTypes: [],
    waveConditions: [],
    windConditions: [],
    waterConditions: [],
    safety: [],
    seaFloor: [],
    environmental: [],
    experience: [],
    cost: [],
    amenities: [],
    seasons: [],
    climate: [],
    food: [],
    alternativeActivities: [],
    regions: [],
    overallScore: 1,
    city: "",
    cityRadius: 100,
  })
  
  const [selectedCity, setSelectedCity] = useState("")
  const [radius, setRadius] = useState([100])
  const [showRadiusFilter, setShowRadiusFilter] = useState(false)
  const [selectedActivity, setSelectedActivity] = useState<"surfing" | "kitesurfing" | "both">("surfing")
  const [overallScore, setOverallScore] = useState([1])
  
  useEffect(() => {
    if (!authLoading && !user && showMobileFilters) {
      router.push("/auth/register")
    }
  }, [user, authLoading, showMobileFilters, router])
  
  useEffect(() => {
    setFilterState(prev => ({
      ...prev,
      activityType: selectedActivity,
      overallScore: overallScore[0],
      city: selectedCity,
      cityRadius: radius[0],
    }))
  }, [selectedActivity, overallScore, selectedCity, radius])
  
  const toggleFilter = (category: keyof FilterState, value: string) => {
    if (!user) {
      router.push("/auth/register")
      return
    }
    triggerHaptic('selection')
    setFilterState(prev => {
      const currentArray = (prev[category] as string[]) || []
      const newArray = currentArray.includes(value)
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value]
      return { ...prev, [category]: newArray }
    })
  }
  
  const isFilterSelected = (category: keyof FilterState, value: string): boolean => {
    const array = filterState[category] as string[]
    return array?.includes(value) || false
  }
  
  const handleApplyFilters = () => {
    if (!user) {
      router.push("/auth/register")
      return
    }
    triggerHaptic('impact')
    onFiltersApply?.(filterState)
    if (isMobile) {
      setShowMobileFilters?.(false)
    }
  }
  
  const handleResetFilters = () => {
    triggerHaptic('impact')
    setFilterState({
      activityType: null, breakTypes: [], skillLevels: [], swellDirections: [], windDirections: [], tides: [], crowdLevels: [], parking: [], launchTypes: [], waveConditions: [], windConditions: [], waterConditions: [], safety: [], seaFloor: [], environmental: [], experience: [], cost: [], amenities: [], seasons: [], climate: [], food: [], alternativeActivities: [], regions: [], overallScore: 1, city: "", cityRadius: 100,
    })
    setSelectedActivity("surfing")
    setOverallScore([1])
    setSelectedCity("")
    setRadius([100])
  }

  const Section = ({ title, icon: Icon, color, children }: { title: string, icon: any, color: string, children: React.ReactNode }) => (
    <div className="space-y-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-[2rem] border border-slate-100 dark:border-slate-800 transition-all hover:bg-white dark:hover:bg-slate-800">
      <div className="flex items-center gap-2">
        <Icon className={cn("w-4 h-4", color)} />
        <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-900 dark:text-white italic">{title}</h3>
      </div>
      {children}
    </div>
  )

  const FilterButton = ({ active, onClick, emoji, label, icon: Icon }: { active: boolean, onClick: () => void, emoji?: string, label: string, icon?: any }) => (
    <Button
      variant={active ? "default" : "outline"}
      onClick={onClick}
      className={cn(
        "h-10 px-3 justify-start rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
        active 
          ? "bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/20 border-none scale-[1.02]" 
          : "bg-white/50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-700 text-slate-500 hover:text-blue-600 hover:bg-blue-50/50"
      )}
    >
      {Icon && <Icon className="w-3.5 h-3.5 mr-2" />}
      {emoji && <span className="mr-2 text-sm">{emoji}</span>}
      <span className="truncate italic">{label}</span>
    </Button>
  )

  if (!authLoading && !user) return null
  
  // On desktop, if not showing, don't render at all to avoid any layout shifts or "ghost" elements
  if (!isMobile && !showMobileFilters) return null

  return (
    <aside
      className={cn(
        "bg-white/95 dark:bg-slate-950/95 backdrop-blur-2xl p-6 overflow-y-auto h-screen sticky top-0",
        "shadow-2xl shadow-blue-900/10",
        isMobile && showMobileFilters 
          ? "w-full fixed left-0 top-0 bottom-0 z-[100] translate-x-0" 
          : isMobile 
            ? "hidden" 
            : "w-80 absolute z-20 translate-x-0 rounded-[3rem] border border-white/20 dark:border-slate-800 mt-6 shadow-2xl ring-1 ring-black/5"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tighter italic leading-none">Filters</h2>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1 italic">Refine results</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleResetFilters}
            className="w-10 h-10 rounded-xl text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
          <Button
            size="icon"
            onClick={handleApplyFilters}
            className="w-10 h-10 bg-blue-600 hover:bg-blue-500 text-white rounded-xl shadow-lg shadow-blue-600/30 border-none transition-all hover:scale-105 active:scale-95"
          >
            <Check className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-4 pb-20">
        {/* Near Me */}
        <div className="p-6 bg-blue-600 rounded-[2.5rem] text-white shadow-xl shadow-blue-600/30 relative overflow-hidden group">
          <div className="absolute top-[-20%] right-[-20%] w-[50%] h-[50%] bg-white/10 blur-3xl rounded-full" />
          <div className="relative z-10 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Locate className="w-5 h-5" />
                <h3 className="text-xs font-black uppercase tracking-widest italic">Near Me</h3>
              </div>
              <Button
                size="sm"
                onClick={() => { triggerHaptic('impact'); onNearMeToggle?.(!nearMeEnabled); }}
                className={cn(
                  "h-8 px-4 rounded-full font-black uppercase tracking-widest text-[8px] transition-all",
                  nearMeEnabled 
                    ? "bg-white text-blue-600 hover:bg-white/90" 
                    : "bg-blue-500/50 text-white border border-white/20 hover:bg-blue-500/70"
                )}
              >
                {nearMeEnabled ? "ACTIVE" : "ENABLE"}
              </Button>
            </div>
            {nearMeEnabled && (
              <div className="space-y-4">
                <Slider
                  value={[nearMeRadius]}
                  onValueChange={(v) => onNearMeRadiusChange?.(v[0])}
                  max={200} min={5} step={5}
                  className="w-full"
                />
                <div className="flex justify-between text-[10px] font-black uppercase tracking-widest opacity-80 italic">
                  <span>5KM</span>
                  <span>{nearMeRadius}KM</span>
                  <span>200KM</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Activity Type */}
        <Section title="Activity" icon={Activity} color="text-blue-500">
          <div className="grid grid-cols-1 gap-2">
            {[
              { label: "Surfing", value: "surfing" as const, emoji: "🏄‍♂️" },
              { label: "Kitesurfing", value: "kitesurfing" as const, emoji: "🪁" },
              { label: "Both", value: "both" as const, emoji: "⚡" }
            ].map((a) => (
              <FilterButton
                key={a.value}
                active={selectedActivity === a.value}
                onClick={() => { triggerHaptic('selection'); setSelectedActivity(a.value); }}
                emoji={a.emoji}
                label={a.label}
              />
            ))}
          </div>
        </Section>

        {/* Overall Score */}
        <Section title="Minimum Rating" icon={Star} color="text-yellow-500">
          <div className="px-2 pt-2">
            <Slider
              value={overallScore}
              onValueChange={setOverallScore}
              max={5} min={1} step={0.5}
            />
            <div className="flex justify-between mt-3 text-[10px] font-black uppercase tracking-widest text-slate-400 italic">
              <span>1 ⭐</span>
              <span className="text-slate-900 dark:text-white">{overallScore[0]} STARS</span>
              <span>5 ⭐</span>
            </div>
          </div>
        </Section>

        {/* Distance from City */}
        <Section title="Proximity" icon={Navigation} color="text-red-500">
          <Input
            placeholder="Search City..."
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            className="h-10 rounded-xl bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 text-[10px] font-black uppercase tracking-widest italic"
          />
          {selectedCity && (
            <div className="space-y-4 pt-2">
              <Slider value={radius} onValueChange={setRadius} max={500} min={25} step={25} />
              <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-slate-400 italic">
                <span>25KM</span>
                <span className="text-slate-900 dark:text-white">{radius[0]}KM</span>
                <span>500KM</span>
              </div>
            </div>
          )}
        </Section>

        {/* Break Types */}
        {(selectedActivity === "surfing" || selectedActivity === "both") && (
          <Section title="Break Type" icon={Waves} color="text-blue-400">
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Beach Break", emoji: "🏖️" },
                { label: "Reef Break", emoji: "🪸" },
                { label: "Point Break", emoji: "🌊" },
                { label: "River Mouth", emoji: "🏞️" }
              ].map((f) => (
                <FilterButton
                  key={f.label}
                  active={isFilterSelected("breakTypes", f.label)}
                  onClick={() => toggleFilter("breakTypes", f.label)}
                  emoji={f.emoji}
                  label={f.label}
                />
              ))}
            </div>
          </Section>
        )}

        {/* Skill Levels */}
        <Section title="Experience" icon={TrendingUp} color="text-purple-500">
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "Beginner", emoji: "🟢" },
              { label: "Intermediate", emoji: "🟡" },
              { label: "Advanced", emoji: "🟠" },
              { label: "Expert", emoji: "🔴" }
            ].map((f) => (
              <FilterButton
                key={f.label}
                active={isFilterSelected("skillLevels", f.label)}
                onClick={() => toggleFilter("skillLevels", f.label)}
                emoji={f.emoji}
                label={f.label}
              />
            ))}
          </div>
        </Section>

        {/* Regions */}
        <Section title="Global Search" icon={Globe} color="text-green-500">
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "North America", flag: "🇺🇸" },
              { label: "Europe", flag: "🇪🇺" },
              { label: "Asia", flag: "🌏" },
              { label: "Oceania", flag: "🇦🇺" },
              { label: "South America", flag: "🇧🇷" },
              { label: "Africa", flag: "🌍" }
            ].map((r) => (
              <FilterButton
                key={r.label}
                active={isFilterSelected("regions", r.label)}
                onClick={() => toggleFilter("regions", r.label)}
                emoji={r.flag}
                label={r.label}
              />
            ))}
          </div>
        </Section>
      </div>

      {isMobile && (
        <Button
          onClick={() => setShowMobileFilters?.(false)}
          className="fixed bottom-10 left-6 right-6 h-14 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-black uppercase tracking-widest text-xs italic shadow-2xl z-50"
        >
          Close Filters
        </Button>
      )}
    </aside>
  )
}
